import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { api } from "../api/client";
import { keys, useInvalidate, useLocations } from "../api/hooks";
import { apiClientKeyResponseSchema, apiClientsResponseSchema, type ApiClient } from "../../api/contracts";
import { formatInstant } from "../lib/format";
import { Badge, Banner, Button, Dialog, ErrorNote, Field, Input, PageHeader, Panel, Spinner, Toggle } from "../components/ui";
import { DataTable } from "../components/DataTable";

/** Partner API keys (spec 2026-10-15). A key can remove BTB blocks at the locations it covers, so only super admins see this. */
export function ApiClientsPage() {
  const invalidate = useInvalidate();
  const list = useQuery({ queryKey: keys.admin.apiClients, queryFn: () => api("/api/admin/api-clients", apiClientsResponseSchema) });
  const locs = useLocations();
  const [name, setName] = useState("");
  const [allLocations, setAllLocations] = useState(true);
  const [picked, setPicked] = useState<string[]>([]);
  const [shownKey, setShownKey] = useState<{ name: string; key: string } | null>(null);
  const [confirm, setConfirm] = useState<{ kind: "revoke" | "rotate"; client: ApiClient } | null>(null);

  const onKey = (r: { client: ApiClient; key: string }) => { setShownKey({ name: r.client.name, key: r.key }); void invalidate(keys.admin.apiClients); };
  const create = useMutation({
    mutationFn: () => api("/api/admin/api-clients", apiClientKeyResponseSchema, { method: "POST", body: { name: name.trim(), locationIds: allLocations ? null : picked } }),
    onSuccess: (r) => { setName(""); setPicked([]); setAllLocations(true); onKey(r); },
  });
  const act = useMutation({
    mutationFn: async (c: { kind: "revoke" | "rotate"; client: ApiClient }) => c.kind === "rotate"
      ? api(`/api/admin/api-clients/${c.client.id}/rotate`, apiClientKeyResponseSchema, { method: "POST" })
      : api(`/api/admin/api-clients/${c.client.id}/revoke`, z.undefined(), { method: "POST" }),
    onSuccess: (r) => { setConfirm(null); if (r) onKey(r); else void invalidate(keys.admin.apiClients); },
  });

  if (list.isPending) return <Spinner />;
  if (list.isError) return <ErrorNote error={list.error} />;
  const nameOf = new Map((locs.data ?? []).map((l) => [l.blvdId, l.name]));
  const where = (c: ApiClient) => c.locationIds === null ? "All locations" : c.locationIds.map((id) => nameOf.get(id) ?? id).join(", ");
  const active = list.data.clients.filter((c) => c.revokedAt === null);
  const revoked = list.data.clients.filter((c) => c.revokedAt !== null);

  return (
    <>
      <PageHeader title="API keys" subtitle="Apps that can check and remove BTB blocks for a booking, such as the AI booking flow. Keys are shown once; store them in the other app's secrets." />
      {shownKey && (
        <div className="mb-4"><Banner tone="warning">
          <p>New key for <strong>{shownKey.name}</strong>. Copy it now; it will not be shown again.</p>
          <code className="mt-1 block break-all font-mono text-[13px]">{shownKey.key}</code>
          <div className="mt-2 flex gap-2">
            <Button size="sm" variant="secondary" onClick={() => void navigator.clipboard?.writeText(shownKey.key)}>Copy</Button>
            <Button size="sm" variant="ghost" onClick={() => setShownKey(null)}>Done</Button>
          </div>
        </Banner></div>
      )}
      <Panel title="New key">
        <form className="grid gap-3" onSubmit={(e) => { e.preventDefault(); if (name.trim()) create.mutate(); }}>
          <Field label="App name"><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Coleman AI booking" /></Field>
          <div className="flex items-center gap-2 text-[13px]"><Toggle checked={allLocations} onChange={setAllLocations} label="Every location" /><span>Every location</span></div>
          {!allLocations && (
            <fieldset className="grid max-h-56 gap-1 overflow-y-auto rounded border border-hs-border p-2 sm:grid-cols-2">
              <legend className="sr-only">Locations</legend>
              {(locs.data ?? []).map((l) => (
                <label key={l.blvdId} className="flex items-center gap-2 text-[13px]">
                  <input type="checkbox" checked={picked.includes(l.blvdId)} onChange={(e) => setPicked((p) => e.target.checked ? [...p, l.blvdId] : p.filter((x) => x !== l.blvdId))} />
                  {l.name}
                </label>
              ))}
            </fieldset>
          )}
          <div><Button type="submit" disabled={create.isPending || !name.trim() || (!allLocations && picked.length === 0)}>Create key</Button></div>
          <ErrorNote error={create.error} />
        </form>
      </Panel>

      <Panel className="mt-4" title="Active keys" padding="none">
        {active.length === 0 ? <p className="p-4 text-[13px] text-hs-text-muted">No keys yet.</p> : (
          <DataTable rows={active} rowKey={(c) => c.id} density="compact" caption="Active API keys" columns={[
            { key: "name", header: "App", cell: (c) => <div><span className="font-medium">{c.name}</span><p className="font-mono text-xs text-hs-text-muted">{c.keyPrefix}…</p></div> },
            { key: "where", header: "Locations", cell: (c) => where(c) },
            { key: "used", header: "Last used", cell: (c) => c.lastUsedAt ? formatInstant(c.lastUsedAt) : "Never" },
            { key: "by", header: "Created", cell: (c) => <span className="text-xs">{c.createdBy}<br />{formatInstant(c.createdAt)}</span> },
            { key: "actions", header: "", headerLabel: "Actions", align: "right", width: "10rem", cell: (c) => <div className="flex justify-end gap-1">
              <Button variant="ghost" size="sm" aria-label={`Rotate ${c.name}`} onClick={() => setConfirm({ kind: "rotate", client: c })}>Rotate</Button>
              <Button variant="ghost" size="sm" aria-label={`Revoke ${c.name}`} onClick={() => setConfirm({ kind: "revoke", client: c })}>Revoke</Button>
            </div> },
          ]} />
        )}
      </Panel>

      {revoked.length > 0 && (
        <Panel className="mt-4" title="Revoked" padding="none">
          <DataTable rows={revoked} rowKey={(c) => c.id} density="compact" caption="Revoked API keys" columns={[
            { key: "name", header: "App", cell: (c) => <span>{c.name} <span className="font-mono text-xs text-hs-text-muted">{c.keyPrefix}…</span></span> },
            { key: "when", header: "Revoked", cell: (c) => <Badge tone="neutral">{formatInstant(c.revokedAt!)}</Badge> },
          ]} />
        </Panel>
      )}

      <Dialog open={confirm !== null} title={confirm ? `${confirm.kind === "rotate" ? "Rotate" : "Revoke"} the key for ${confirm.client.name}?` : ""} onClose={() => setConfirm(null)}
        footer={<><Button variant="secondary" disabled={act.isPending} onClick={() => setConfirm(null)}>Cancel</Button>
          <Button variant="danger" disabled={act.isPending} onClick={() => confirm && act.mutate(confirm)}>{confirm?.kind === "rotate" ? "Yes, rotate" : "Yes, revoke"}</Button></>}>
        <p>{confirm?.kind === "rotate" ? "The current key stops working now and a new one is shown once. Update the other app straight away." : "The app can no longer check or remove BTB blocks."}</p>
        <ErrorNote error={act.error} />
      </Dialog>
    </>
  );
}
